import { getCountryByCountryCode } from '@app/ducks';
import { isDefined } from '@app/utils/is';
import { isEmpty } from '@app/utils/isEmpty';
import { Addresses } from '@typings';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

interface Props {
  address: Addresses.Basic;
}

const shouldShowData = (data?: Nullable<string>) => isDefined(data) && !isEmpty(data);
const showData = (data?: Nullable<string>) => shouldShowData(data) && data;

export const CheckoutSuccessShippingAddress = ({ address }: Props) => {
  const { t } = useTranslation(['common', 'shipping']);

  const country = isDefined(address?.country) ? address.country : '';
  const shippingCountryCodeSelector = React.useMemo(() => getCountryByCountryCode(country), [country]);
  const shippingCountry = useSelector(shippingCountryCodeSelector);

  const stateName = isDefined(address?.state) ? shippingCountry.states?.[address.state]?.name ?? '' : '';
  const shouldShowContactPersonData = shouldShowData(address?.contactPerson) || shouldShowData(address?.phoneNumber);

  return (
    <div className="box">
      <h3 className="mb-5 heading-3">{t('shipping:shipping_address_one')}</h3>
      <div
        data-sentry-mask
        className="relative flex w-full flex-col gap-5 rounded-regular bg-neutral-96/85 p-5 text-neutral-20 sm:w-fit sm:min-w-72"
      >
        {(address.company || address.email) && (
          <div className="flex flex-col">
            {address.company && <span className="truncate heading-3">{address.company}</span>}
            {address.email && (
              <a href={`mailto:${address.email}`} className="truncate text-neutral-40 underline underline-offset-2 body-2">
                {address.email}
              </a>
            )}
          </div>
        )}
        <div className="flex flex-col break-words">
          <span className="body-6">{t('common:address')}</span>
          <span className="body-5">
            {address.address1} {address.address2}
          </span>
          <span className="body-5">
            {address.zipCode} {address.city}
          </span>
          <span className="body-5">
            {stateName}
            {shippingCountry.name}
          </span>
        </div>
        {shouldShowContactPersonData && (
          <div className="flex flex-col break-words">
            <span className="body-6">{t('common:contact_person')}</span>
            <span className="body-5">{showData(address.contactPerson)}</span>
            <span className="body-5">{showData(address.phoneNumber)}</span>
          </div>
        )}
      </div>
    </div>
  );
};
